import Close from '@mui/icons-material/Close'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  bgcolor: 'background.paper',
  borderRadius: 4,
  boxShadow: 24,
  p: 4,
}

interface ExclusionAreaObservationModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: { type: number; comment: string }) => void
  loading?: boolean
}

export const ExclusionAreaObservationModal: React.FC<ExclusionAreaObservationModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [comment, setComment] = useState('')
  const [observationTypeSelected, setObservationTypeSelected] = useState<string | null>(null)
  const [nonComplianceTypeSelected, setNonComplianceTypeSelected] = useState<string | null>(null)

  const handleClose = () => {
    setObservationTypeSelected(null)
    setNonComplianceTypeSelected(null)
    setComment('')
    onClose()
  }

  const resolvedType = (): number | null => {
    if (observationTypeSelected === '0') return 0
    if (observationTypeSelected === '1' && nonComplianceTypeSelected !== null) {
      return Number(nonComplianceTypeSelected)
    }
    return null
  }

  const isConfirmDisabled = resolvedType() === null || comment.trim() === ''

  const handleConfirm = () => {
    const type = resolvedType()
    if (type === null) return
    onConfirm({ type, comment })
    setObservationTypeSelected(null)
    setNonComplianceTypeSelected(null)
    setComment('')
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Grid container spacing={2}>
          <Grid item xs={11}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, margin: 2 }}>
              Nueva observación de Área de Exclusión
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Close
              sx={{ float: 'right', margin: 2, cursor: 'pointer', '&:hover': { color: 'red' } }}
              onClick={handleClose}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item>
            <FormControl sx={{ m: 2 }}>
              <Typography alignItems="flex-start" display="flex" fontSize={16} m={1}>
                Tipo de observación
              </Typography>
              <RadioGroup
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.value !== '1') {
                    setNonComplianceTypeSelected(null)
                  }
                  setObservationTypeSelected(e.target.value)
                }}
              >
                <FormControlLabel
                  control={<Radio sx={{ '&.Mui-checked': { color: '#e9e925' } }} />}
                  label="Observación"
                  value={0}
                />
                <FormControlLabel
                  control={<Radio sx={{ '&.Mui-checked': { color: 'grey' } }} />}
                  label="No conformidad"
                  value={1}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {observationTypeSelected === '1' && (
            <Grid item>
              <FormControl sx={{ m: 2 }}>
                <Typography alignItems="flex-start" display="flex" fontSize={16} m={1}>
                  Tipo de no conformidad
                </Typography>
                <RadioGroup
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNonComplianceTypeSelected(e.target.value)
                  }
                >
                  <FormControlLabel
                    control={<Radio sx={{ '&.Mui-checked': { color: 'orange' } }} />}
                    label="A corregir"
                    value={1}
                  />
                  <FormControlLabel
                    control={<Radio sx={{ '&.Mui-checked': { color: 'red' } }} />}
                    label="Grave"
                    value={2}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <FormControl sx={{ m: 2 }}>
          <Typography alignItems="flex-start" display="flex" fontSize={16} m={1}>
            Comentario
          </Typography>
          <TextField
            multiline
            inputProps={{ maxLength: 255 }}
            placeholder="Comentario"
            rows={6}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
        </FormControl>

        <Grid container>
          <Grid item xs={6}>
            <Button
              disabled={isConfirmDisabled}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                borderRadius: 4,
                margin: 2,
                px: 2,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#1976d2', color: 'white', opacity: 0.8 },
                '&:disabled': { backgroundColor: 'grey', color: 'white', opacity: 0.8 },
              }}
              onClick={handleConfirm}
            >
              Confirmar
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              sx={{
                color: '#1976d2',
                backgroundColor: 'white',
                margin: 2,
                px: 2,
                fontWeight: 600,
                textTransform: 'none',
              }}
              onClick={handleClose}
            >
              Cancelar
            </Button>
          </Grid>
        </Grid>

        <LinearProgress sx={{ display: loading ? 'block' : 'none' }} />
      </Box>
    </Modal>
  )
}
